import { checkIfDefined } from 'legacy/lut/helpers/checkIfDefined'
import { checkRange } from 'legacy/lut/helpers/validateRules/checkRange'
import React, { memo } from 'react'

import { IFormDropdownProps } from 'cng-common/features/form/components/formDropdown/models'
import { IFormNumberInput } from 'cng-common/features/form/components/formNumberInput/models'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { IPartnerTerritory } from 'onboarding/classes/models/partnerTerritoryModels'
import {
  FormLocationTreeAdapter,
  OnboardingFormDropdownAdapter,
  OnboardingFormNumberInputAdapter,
} from 'onboarding/components/ui/visualAudienceAdapters'

import { TFormLocationTreeExtended } from '../formLocationTreeExtended/FormLocationTreeExtended'
import { TDropdownsCruds } from '../models'
import styles from '../RequestorFill.module.scss'

import { DealGuidelines } from './DealGuidelines'

import dealInfoStyles from './DealInfo.module.scss'

interface IProps {
  formKey: string
  partnerTerritory: IPartnerTerritory
  cruds: TDropdownsCruds
  hpSigningEntity: string | null
  territoryOfSaleSelected: string[]
  territoryOfSaleRoot?: string
  dealValue: number | null
  dealMargin: number | null
  winProbability: number | null
  salesStage: string | null
  writeAudience: IAudienceCondition
  isDisabled?: boolean
}

const MIN_PERCENT = 0
const MAX_PERCENT = 100
const checkPercentRangeIfDefined = (value: number | null) =>
  checkIfDefined<number | null>((value: number) =>
    checkRange({
      value,
      min: MIN_PERCENT,
      max: MAX_PERCENT,
    })
  )(value)

export const FormFields = memo(
  ({
    formKey,
    partnerTerritory,
    cruds,
    hpSigningEntity,
    territoryOfSaleSelected,
    territoryOfSaleRoot,
    dealValue,
    dealMargin,
    winProbability,
    salesStage,
    writeAudience,
    isDisabled,
  }: IProps) => {
    return (
      <div>
        <div className={styles.wrapper}>
          <VisualAudienceExtended<IFormDropdownProps<any>>
            write={writeAudience}
            formKey={formKey}
            component={OnboardingFormDropdownAdapter}
            label={'HP Signing Entity'}
            field={'HPSigningEntity'}
            source={cruds.hpSigningEntityApj}
            defaultValue={hpSigningEntity || ''}
            caption={'en'}
            isDisabled={isDisabled}
            isRequired
          />
        </div>

        <div className={styles.wrapper}>
          <VisualAudienceExtended<TFormLocationTreeExtended>
            write={writeAudience}
            formKey={formKey}
            partnerTerritory={partnerTerritory}
            component={FormLocationTreeAdapter}
            label={'Territory of Sale'}
            field={'TerritorySaleISO'}
            defaultValue={territoryOfSaleSelected}
            root={territoryOfSaleRoot}
            isDisabled={isDisabled}
            isRequired
          />
        </div>

        <div className={dealInfoStyles.root}>
          <div>Deal Information (fill in ONLY for Once Off-Deal agreement relationship)</div>
          <br />

          <div className={styles.wrapper}>
            <VisualAudienceExtended<IFormNumberInput>
              write={writeAudience}
              formKey={formKey}
              component={OnboardingFormNumberInputAdapter}
              label={'Deal value in US$'}
              field={'DealValue'}
              defaultValue={dealValue}
              isDisabled={isDisabled}
            />
          </div>

          <div className={styles.wrapper}>
            <VisualAudienceExtended<IFormNumberInput>
              write={writeAudience}
              formKey={formKey}
              component={OnboardingFormNumberInputAdapter}
              label={`HP deal margin (${MIN_PERCENT}-${MAX_PERCENT}%)* (%)`}
              field={'DealMargin'}
              defaultValue={dealMargin}
              onValidate={checkPercentRangeIfDefined}
              isDisabled={isDisabled}
            />
          </div>

          <div className={styles.wrapper}>
            <VisualAudienceExtended<IFormDropdownProps<any>>
              write={writeAudience}
              formKey={formKey}
              component={OnboardingFormDropdownAdapter}
              label={'Sales stage'}
              field={'SalesStage'}
              source={cruds.salesStage}
              defaultValue={salesStage || ''}
              caption={'en'}
              isDisabled={isDisabled}
            />
          </div>

          <div className={styles.wrapper}>
            <VisualAudienceExtended<IFormNumberInput>
              write={writeAudience}
              formKey={formKey}
              component={OnboardingFormNumberInputAdapter}
              label={`Win probability (${MIN_PERCENT}-${MAX_PERCENT}%)* (%)`}
              field={'WinProbability'}
              defaultValue={winProbability}
              onValidate={checkPercentRangeIfDefined}
              isDisabled={isDisabled}
            />
          </div>

          <DealGuidelines />
        </div>
      </div>
    )
  }
)
